import React, { useRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Form, { GroupItem, SimpleItem, Item, RequiredRule, RangeRule } from "devextreme-react/form";
import { NumberBox } from "devextreme-react/number-box";
import { Popup } from "devextreme-react/popup";
import TextArea from "devextreme-react/cjs/text-area";
import { Button, FileUploader, SelectBox, TextBox } from "devextreme-react";
import dxForm from "devextreme/ui/form";

import { CustomerModel } from "@/shared/models/Customer";
import { useSafeFormFieldUpdater } from "@/hooks/useSafeFormFieldUpdater";
import { ReviewCustomersApi } from "@/shared/api/ReviewCustomers.api";
import { selectSalesPersonDataSource } from "@/redux/salesRepSlice";
import { convertDeterminantsToJsonString } from "@/shared/business/ReviewCustomers";
import { Utilities } from "@/shared/utils/utilities";
import { DateTimeFormatStrings } from "@/shared/enums";
import { CustomerAttributeConstraint, EmptyCustomerAttributeConstraint } from "@/shared/models/CustomerAttributeConstraint";
import { getFormulaTypes } from "@/shared/api/Formula.api";
import { FormulaType } from "@/shared/models/FormulaType";
import { useCustomerAttributeConstraint } from "@/hooks/useCustomerAttributeConstraint";
import { useFormCloseConfirm } from "@/hooks/useFormCloseConfirm";
import CustomerComment from "./CustomerComment";
import { CustomerNote } from "@/shared/models/CustomerNote";

interface CustomerFormProps {
  visible: boolean;
  eidt: boolean;
  customerData: CustomerModel;
  closeForm: (reloadData: boolean) => Promise<void>;
}

const ReviewCustomersEditForm: React.FC<CustomerFormProps> = ({ visible, eidt, customerData, closeForm }) => {
  const { setInitialFormData, canCloseForm } = useFormCloseConfirm(new CustomerModel());
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerModel>(new CustomerModel());
  const [customerNotes, setCustomerNotes] = useState<CustomerNote[]>([]);

  const salesPeople = useSelector(selectSalesPersonDataSource);
  const updateField = useSafeFormFieldUpdater(setSelectedCustomer);
  const { customerAttributeConstraints, loadCustomerAttributeConstraints } = useCustomerAttributeConstraint();

  const formRef = useRef<dxForm | null | undefined>(null);

  const [isEditMode, setIsEditMode] = useState(eidt);
  const [types, setTypes] = useState<FormulaType[]>([]);
  const [commentType, setCommentType] = useState("Comment");
  const [commentText, setCommentText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [updatedAtText, setUpdatedAtText] = useState("");
  const [sapMaterialProductCodeSelectItems, setSapMaterialProductCodeSelectItems] = useState<CustomerAttributeConstraint[]>([]);

  useEffect(() => {
    if (visible && customerData) {
      const cloned = structuredClone(customerData);
      setSelectedCustomer(cloned);
      setInitialFormData(cloned);
      if (cloned.UpdatedAt != null) {
        const formattedDate = Utilities.formatDate(cloned.UpdatedAt, DateTimeFormatStrings.FORMAT_12);
        setUpdatedAtText(formattedDate);
      }
    }

    loadingData(customerData);
  }, [customerData, visible]);

  const loadingData = async (customer: CustomerModel) => {
    try {
      await loadCustomerAttributeConstraints();
      const formulaData = await getFormulaTypes();
      formulaData && setTypes(formulaData);

      if (customer && customer.ShipTo && customer.ShipTo !== 0) {
        var customerDeterminants = await ReviewCustomersApi.getCustomerDeterminants(customer.ShipTo.toString());
        customerDeterminants && setCustomerNotes(customerDeterminants!.Notes);
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
    }
  };

  // Define Yes/No options mapped to boolean values
  const yesNoOptions = [
    { text: "Yes", value: true },
    { text: "No", value: false },
  ];

  const handleClone = () => {
    var clonedCustomer = {
      ...selectedCustomer,
      ShipTo: null,
      SurchargeStartDate: new Date(),
    };
    setSelectedCustomer(clonedCustomer);
    setInitialFormData(clonedCustomer);
    setIsEditMode(false);
  };

  const handleProductTypeChanged = (productType: string) => {
    setSapMaterialProductCodeSelectItems(
      customerAttributeConstraints.filter((item) => item.type === "SAPMaterialProductCode" && item.description.startsWith(productType))
    );
  };

  const handleSave = async () => {
    if (!selectedCustomer) return;
    const res = formRef.current?.validate();
    if (res?.isValid) {
      try {
        if (isEditMode) {
          selectedCustomer.Create = false;
        } else {
          selectedCustomer.Create = true;
        }

        selectedCustomer.Determinants = convertDeterminantsToJsonString(selectedCustomer);
        const payload: any = { ...selectedCustomer };
        delete payload.DeterminantsProps;
        //      delete payload.customerData;

        if (await ReviewCustomersApi.updateCustomer(payload)) {
          if (commentText !== "" && commentType !== "") {
            await ReviewCustomersApi.addComment(commentText, commentType, selectedCustomer.ShipTo!, selectedFile);
          }

          closeForm && (await closeForm(true));
        }
      } catch (error) {
        console.error("Error saving customer:", error);
      }
    }
  };

  const handleCancel = async () => {
    canCloseForm(selectedCustomer) && closeForm && (await closeForm(false));
  };

  return (
    <>
      <Popup
        visible={visible}
        onHiding={async (e) => {
          e.cancel = true;
          await handleCancel();
        }}
        dragEnabled={true}
        showTitle={true}
        title={isEditMode ? "Update Customer" : "Add New Customer"}
        width="75%" // Large Popup
        height="auto"
      >
        <div className="container scrollable-form" style={{ maxHeight: "75vh", overflowY: "auto", paddingRight: "15px" }}>
          <Form
            formData={selectedCustomer}
            colCount={4}
            labelMode="floating"
            onInitialized={(e) => {
              formRef.current = e.component;
            }}
          >
            {/* 1. Autocomplete Full Width */}
            <GroupItem colSpan={2}>
              <SimpleItem
                dataField="PrimarySalesRep"
                editorType="dxAutocomplete"
                label={{ text: "Primary Sales Rep" }}
                editorOptions={{
                  dataSource: salesPeople,
                  placeholder: "Search value",
                  value: selectedCustomer?.PrimarySalesRep,
                  onValueChanged: (e: any) => updateField("PrimarySalesRep", e.value),
                }}
              >
                <RequiredRule message="Primary Sales Rep is required" />
              </SimpleItem>
            </GroupItem>
            <GroupItem colSpan={2}></GroupItem>
            {/* part 1 */}
            <GroupItem>
              <SimpleItem dataField="ShipTo" editorType="dxNumberBox" label={{ text: "SAP ShipTo # " }} editorOptions={{ disabled: isEditMode }}>
                <RequiredRule></RequiredRule>
                <RangeRule max={2000000000} message="Value must not exceed 2,000,000,000" />
              </SimpleItem>

              <SimpleItem
                dataField="Plant"
                label={{ text: "Source " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: [EmptyCustomerAttributeConstraint, ...customerAttributeConstraints.filter((item: any) => item.type === "Plant")],
                  valueExpr: "code",
                  displayExpr: "code",
                  placeholder: "Select Plant",
                  value: selectedCustomer?.Plant || null,
                  onValueChanged: (e: any) => {
                    updateField("Plant", e.value);
                  },
                }}
              />

              <Item
                render={() => (
                  <NumberBox
                    label="NG Efficiency"
                    value={selectedCustomer?.DeterminantsProps["NG Efficiency"]?.NumericValue || 0}
                    showSpinButtons={true}
                    step={0.01}
                    onValueChanged={(e) => {
                      updateField("DeterminantsProps.NG Efficiency.NumericValue", e.value);
                    }}
                    width="100%"
                  />
                )}
              >
                <RequiredRule></RequiredRule>
              </Item>

              <SimpleItem
                dataField="Frequency"
                label={{ text: "Frequency " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: customerAttributeConstraints.filter((item: any) => item.type === "Frequency"),
                  valueExpr: "code",
                  displayExpr: "code",
                  placeholder: "Select Frequency",
                  value: selectedCustomer?.Frequency || null,
                  onValueChanged: (e: any) => {
                    updateField("Frequency", e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            <GroupItem>
              <SimpleItem dataField="Name" editorType="dxTextBox" label={{ text: "Customer Name " }}>
                <RequiredRule message="Customer Name is required" />
              </SimpleItem>

              <SimpleItem
                dataField="Region"
                label={{ text: "Region " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: [
                    { text: "", value: "" },
                    { text: "North", value: "North" },
                    { text: "South", value: "South" },
                    { text: "East", value: "East" },
                    { text: "West", value: "West" },
                  ],
                  value: selectedCustomer?.Region,
                  displayExpr: "text",
                  valueExpr: "value",
                  placeholder: "Select Region",
                  onValueChanged: (e: any) => {
                    updateField("Region", e.value);
                  },
                }}
              />

              <Item
                render={() => (
                  <NumberBox
                    label="Spec Power"
                    value={selectedCustomer?.DeterminantsProps["SpecPower"]?.NumericValue || 0}
                    showSpinButtons={true}
                    step={0.01}
                    onValueChanged={(e) => {
                      updateField("DeterminantsProps.SpecPower.NumericValue", e.value);
                    }}
                    width="100%"
                  />
                )}
              >
                <RequiredRule></RequiredRule>
              </Item>

              <SimpleItem
                dataField="Currency"
                label={{ text: "Currency " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: customerAttributeConstraints.filter((item: any) => item.type === "Currency"),
                  valueExpr: "code",
                  displayExpr: "code",
                  placeholder: "Select Currency",
                  value: selectedCustomer?.Currency || null,
                  onValueChanged: (e: any) => {
                    updateField("Currency", e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            <GroupItem>
              <SimpleItem
                dataField="SurchargeType"
                label={{ text: "Surcharge Type " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: [
                    { text: "Energy", value: "Energy" },
                    { text: "Product", value: "Product" },
                  ],
                  value: selectedCustomer?.SurchargeType,
                  displayExpr: "text",
                  valueExpr: "value",
                  placeholder: "Select Surcharge Type",
                  onValueChanged: (e: any) => {
                    updateField("SurchargeType", e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>

              <Item
                render={() => (
                  <NumberBox
                    label="Base NG"
                    value={selectedCustomer?.DeterminantsProps["Base NG"]?.NumericValue || 0}
                    showSpinButtons={true}
                    step={0.01}
                    onValueChanged={(e) => {
                      updateField("DeterminantsProps.Base NG.NumericValue", e.value);
                    }}
                    width="100%"
                  ></NumberBox>
                )}
              >
                <RequiredRule></RequiredRule>
              </Item>

              <Item
                render={() => (
                  <NumberBox
                    label="Diesel"
                    value={selectedCustomer?.DeterminantsProps["Diesel"]?.NumericValue || 0}
                    showSpinButtons={true}
                    step={0.01}
                    onValueChanged={(e) => {
                      updateField("DeterminantsProps.Diesel.NumericValue", e.value);
                    }}
                    width="100%"
                  />
                )}
              >
                <RequiredRule></RequiredRule>
              </Item>

              <Item
                render={() => (
                  <NumberBox
                    label="Multiplier"
                    value={selectedCustomer?.Multiplier || 0}
                    showSpinButtons={true}
                    step={0.01}
                    onValueChanged={(e) => {
                      updateField("Multiplier", e.value);
                    }}
                    width="100%"
                  />
                )}
              >
                <RequiredRule></RequiredRule>
              </Item>
            </GroupItem>

            <GroupItem>
              <SimpleItem
                dataField="Formula"
                label={{ text: "Type" }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: types,
                  valueExpr: "name",
                  displayExpr: "name",
                  placeholder: "Select Formula",
                  value: selectedCustomer?.Formula || null,
                  onValueChanged: (e: any) => {
                    updateField("Formula", e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>

              <Item
                render={() => (
                  <NumberBox
                    label="Base Milage"
                    value={selectedCustomer?.DeterminantsProps["Base Milage"]?.NumericValue || 0}
                    showSpinButtons={true}
                    step={0.01}
                    onValueChanged={(e) => {
                      updateField("DeterminantsProps.Base Milage.NumericValue", e.value);
                    }}
                    width="100%"
                  />
                )}
              >
                <RequiredRule></RequiredRule>
              </Item>

              <Item
                render={() => (
                  <NumberBox
                    label="Diesel Recovery"
                    value={selectedCustomer?.DeterminantsProps["DieselRecovery"]?.NumericValue || 0}
                    showSpinButtons={true}
                    step={0.01}
                    onValueChanged={(e) => {
                      updateField("DeterminantsProps.DieselRecovery.NumericValue", e.value);
                    }}
                    width="100%"
                  />
                )}
              >
                <RequiredRule></RequiredRule>
              </Item>

              <SimpleItem
                dataField="Update"
                label={{ text: "Update " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: customerAttributeConstraints.filter((item: any) => item.type === "Update"),
                  valueExpr: "code",
                  displayExpr: "code",
                  placeholder: "Select Update",
                  value: selectedCustomer?.Update || null,
                  onValueChanged: (e: any) => {
                    updateField("Update", e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            {/* part 2 */}
            <GroupItem>
              <SimpleItem
                dataField="SalesOrg"
                label={{ text: "Sales Org " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: customerAttributeConstraints.filter((item: any) => item.type === "SalesOrg"),
                  valueExpr: "code",
                  displayExpr: "code",
                  placeholder: "Select SalesOrg",
                  value: selectedCustomer?.SalesOrg || null,
                  onValueChanged: (e: any) => {
                    updateField("SalesOrg", e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            <GroupItem>
              <SimpleItem
                dataField="ProductType"
                label={{ text: "Product Line " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: [
                    { text: "Nitrogen", value: "Nitrogen" },
                    { text: "Oxygen", value: "Oxygen" },
                    { text: "Helium", value: "Helium" },
                    { text: "Hydrogen", value: "Hydrogen" },
                    { text: "Argon", value: "Argon" },
                    { text: "CO2", value: "CO2" },
                  ],
                  value: selectedCustomer?.ProductType,
                  displayExpr: "text",
                  valueExpr: "value",
                  placeholder: "Select ProductType",
                  onValueChanged: (e: any) => {
                    updateField("ProductType", e.value);
                    handleProductTypeChanged(e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            <GroupItem colSpan={2}>
              <SimpleItem
                dataField="SAPMaterialProductCode"
                label={{ text: "SAP Material Product " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: sapMaterialProductCodeSelectItems,
                  valueExpr: "code",
                  displayExpr: "displayValue",
                  placeholder: "Select SAPMaterialProductCode",
                  value: selectedCustomer?.SAPMaterialProductCode || null,
                  onValueChanged: (e: any) => {
                    updateField("SAPMaterialProductCode", e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            {/* part 3 */}
            <GroupItem>
              <SimpleItem
                dataField="SAPEnergySurchargeCode"
                label={{ text: "SAP Surcharge Code " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: customerAttributeConstraints.filter((item: any) => item.type === "SAPEnergySurchargeCode"),
                  valueExpr: "code",
                  displayExpr: "code",
                  placeholder: "Select SAPEnergySurchargeCode",
                  value: selectedCustomer?.SAPEnergySurchargeCode || null,
                  onValueChanged: (e: any) => {
                    updateField("SAPEnergySurchargeCode", e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            <GroupItem>
              <SimpleItem
                dataField="Unit"
                editorType="dxNumberBox"
                label={{ text: "Unit " }}
                editorOptions={{
                  step: 1,
                  showSpinButtons: true,
                  format: "#",
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            <GroupItem>
              <SimpleItem
                dataField="Uom"
                label={{ text: "UOM " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: customerAttributeConstraints.filter((item: any) => item.type === "UOM"),
                  valueExpr: "code",
                  displayExpr: "code",
                  placeholder: "Select Uom",
                  value: selectedCustomer?.Uom || null,
                  onValueChanged: (e: any) => {
                    updateField("Uom", e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            <GroupItem>
              <SimpleItem
                // dataField="selectedCustomer.DeterminantsProps['NOLA Tax']"
                label={{ text: "NOLA Tax " }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: [
                    { text: "Yes", value: "yes" },
                    { text: "No", value: "no" },
                  ],
                  value: selectedCustomer?.DeterminantsProps["NOLA Tax"]?.StringValue,
                  displayExpr: "text",
                  valueExpr: "value",
                  placeholder: "Select Status",
                  onValueChanged: (e: any) => {
                    updateField("DeterminantsProps.NOLA Tax.StringValue", e.value);
                  },
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            {/* part 4 */}
            <GroupItem>
              <SimpleItem
                dataField="IncludeInNewSurchargeEvents"
                label={{ text: "Active" }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: yesNoOptions,
                  value: selectedCustomer?.IncludeInNewSurchargeEvents,
                  displayExpr: "text",
                  valueExpr: "value",
                  placeholder: "Select Status",
                  onValueChanged: (e: any) => {
                    updateField("IncludeInNewSurchargeEvents", e.value);
                  },
                }}
              ></SimpleItem>
            </GroupItem>

            <GroupItem>
              <SimpleItem
                dataField="SendEmail"
                label={{ text: "Email" }}
                editorType="dxSelectBox"
                editorOptions={{
                  items: yesNoOptions,
                  value: selectedCustomer?.SendEmail,
                  displayExpr: "text",
                  valueExpr: "value",
                  placeholder: "Select Status",
                  onValueChanged: (e: any) => {
                    updateField("SendEmail", e.value);
                  },
                }}
              ></SimpleItem>
            </GroupItem>

            <GroupItem colSpan={2}>
              <SimpleItem
                dataField="SurchargeStartDate"
                label={{ text: "Surcharge Start Date " }}
                editorType="dxDateBox"
                editorOptions={{
                  value: selectedCustomer?.SurchargeStartDate || null, // Bind your date value
                  onValueChanged: (e: any) => {
                    updateField("SurchargeStartDate", e.value);
                  },
                  displayFormat: "MM/dd/yyyy", // Optional: Format
                  type: "date",
                  placeholder: "Select Date",
                }}
              >
                <RequiredRule></RequiredRule>
              </SimpleItem>
            </GroupItem>

            {/* 3. Custom 2-Column Section */}

            {isEditMode && (
              <>
                <Item colSpan={4} cssClass="form-section-topmargin">
                  <div className="row">
                    <div className="col-md-6">
                      <label>Comments</label>
                      <CustomerComment notes={customerNotes}></CustomerComment>
                    </div>

                    <div className="col-md-6">
                      <label>Comment Type</label>
                      <SelectBox
                        items={["Comment", "Customer Added", "Customer Removed", "Warning"]}
                        value={commentType}
                        onValueChanged={(e: any) => setCommentType(e.value)}
                        style={{ width: "100%", marginBottom: "10px" }}
                      />

                      <div className="margin-top10">
                        <label>Comment Text</label>
                        <TextArea height={80} maxLength={100} value={commentText} onValueChanged={(e: any) => setCommentText(e.value)} />
                      </div>
                      <div className="margin-top10">
                        <label>Upload Document</label>
                        <FileUploader
                          selectButtonText="Choose File"
                          labelText=""
                          accept="*"
                          showFileList={true}
                          uploadMode="useForm"
                          onValueChanged={(e) => {
                            const file = e.value?.[0];
                            if (file) {
                              setSelectedFile(file);
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </Item>
                <Item colSpan={4} cssClass="form-section-topmargin">
                  <div className="row">
                    <div className="col-md-6">
                      <label>Changed on:</label>
                      <TextBox disabled={true} value={updatedAtText} />
                    </div>
                    <div className="col-md-6">
                      <label>Changes by:</label>
                      <TextBox disabled={true} value={selectedCustomer.UpdatedBy} />
                    </div>
                  </div>
                </Item>
              </>
            )}
          </Form>

          <div className="text-end mt-3">
            {isEditMode && (
              <Button width={70} type="danger" className="me-2" onClick={handleClone}>
                CLONE
              </Button>
            )}
            <Button width={70} type="default" className="me-2" onClick={handleSave}>
              {isEditMode ? "SUBMIT" : "CREATE"}
            </Button>
            <Button width={70} onClick={handleCancel}>
              CANCEL
            </Button>
          </div>
        </div>
      </Popup>
    </>
  );
};

export default ReviewCustomersEditForm;
